// Web Audio recording + playback + analysis helpers
import lamejs from "lamejs";
// lamejs 1.2.1 bug: Mp3Encoder references MPEGMode/Lame/BitStream as bare
// globals that the npm build never defines — encoding throws
// "MPEGMode is not defined" without these shims.
import MPEGMode from "lamejs/src/js/MPEGMode";
import Lame from "lamejs/src/js/Lame";
import BitStream from "lamejs/src/js/BitStream";
import { logger } from "./logger";

window.MPEGMode = MPEGMode;
window.Lame = Lame;
window.BitStream = BitStream;

// ---------- Audio constants ----------
// Extracted from inline magic numbers to make the DSP intent explicit and give
// future callers a single knob per parameter.
const SAMPLE_RATE = 44100;             // Hz — offline OfflineAudioContext render rate.
const LEVEL_METER_FFT_SIZE = 512;      // bins for the record-time RMS level meter (small = low latency).
const HIT_CENTROID_KICK_MAX = 1400;    // Hz — spectral centroid below this → kick
const HIT_CENTROID_SNARE_MAX = 3500;   // Hz — centroid below this (but ≥ kick threshold) → snare; else hat

// Ordered fallback list for MediaRecorder MIME types — first supported wins.
// Extracted from a nested ternary for readability.
const RECORDER_MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
];
function pickRecorderMime() {
  for (const mime of RECORDER_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return "audio/mp4";
}

let _ac = null;
export function getAudioContext() {
  if (!_ac) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    _ac = new Ctx();
  }
  return _ac;
}

export async function recordAudio(onLevel, { deferStart = false } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = pickRecorderMime();
  const rec = new MediaRecorder(stream, { mimeType: mime });
  const chunks = [];

  const ctx = getAudioContext();
  if (ctx.state === "suspended") await ctx.resume();
  const src = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = LEVEL_METER_FFT_SIZE;
  src.connect(analyser);
  const data = new Uint8Array(analyser.frequencyBinCount);
  let raf;
  const tick = () => {
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / data.length);
    if (onLevel) onLevel(Math.min(1, rms * 3));
    raf = requestAnimationFrame(tick);
  };
  tick();

  rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunks.push(e.data);
  // In deferStart mode the caller invokes .start() at the precise moment they
  // want capture to begin (e.g. immediately after the count-in downbeat). This
  // avoids the 100–300 ms getUserMedia + MediaRecorder construction latency
  // that would otherwise chop the first hit off the take.
  if (!deferStart) rec.start();

  return {
    mime,
    start: () => { if (rec.state === "inactive") rec.start(); },
    stop: () =>
      new Promise((resolve) => {
        rec.onstop = () => {
          cancelAnimationFrame(raf);
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(chunks, { type: mime });
          resolve(blob);
        };
        rec.stop();
      }),
  };
}

export async function decodeBlob(blob) {
  const ctx = getAudioContext();
  const buf = await blob.arrayBuffer();
  return await ctx.decodeAudioData(buf);
}

// Best-effort headphone detection based on the labels the browser exposes
// via `enumerateDevices()`. Only works after some audio permission (mic) has
// been granted — before that all labels are empty strings. Safari on macOS +
// iOS never exposes `audiooutput` devices at all so `hasLabels` will be false
// and callers fall back to a manual toggle.
//
// Returns { hasHeadphones, hasSpeakers, hasLabels, sample } — `sample` is a
// short list of the detected output labels for the debug tooltip.
const HEADPHONE_RE = /headphone|headset|airpods|earbud|earphone|earpiece|bluetooth|beats|freebuds|pixel\s?buds|galaxy\s?buds|neckband|wh-\d|hd\s?\d|momentum|sennheiser|jabra|jbl|bose|sony\s?wh|akg|shure|iem|monitors/i;
const SPEAKER_RE = /speaker|internal|built-?in|macbook|imac|display audio|hdmi|realtek|nvidia|thunderbolt|monitor\s?audio/i;
export async function detectHeadphones() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
    return { hasHeadphones: false, hasSpeakers: false, hasLabels: false, sample: [] };
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const outputs = devices.filter((d) => d.kind === "audiooutput");
    const labels = outputs.map((d) => (d.label || "").trim()).filter(Boolean);
    const hasHeadphones = labels.some((l) => HEADPHONE_RE.test(l));
    // If we saw at least one non-empty label AND every non-headphone label
    // matches the speaker pattern, we're confident speakers are the only
    // output. If the mix is ambiguous (unknown label), don't claim speakers.
    const speakerOnly = labels.length > 0 && !hasHeadphones
      && labels.every((l) => SPEAKER_RE.test(l));
    return {
      hasHeadphones,
      hasSpeakers: speakerOnly,
      hasLabels: labels.length > 0,
      sample: labels.slice(0, 4),
    };
  } catch {
    return { hasHeadphones: false, hasSpeakers: false, hasLabels: false, sample: [] };
  }
}

// Sample-accurate trim of an AudioBuffer down to a fixed duration (seconds).
// Used to (a) enforce the 4-bar recording ceiling — codec priming / MediaRecorder
// stop latency can push the decoded buffer a few dozen ms past the loop window,
// which cascades into "5 bars showing in Edit Bars"; and (b) chop off the +1 s
// of trailing tail-room the OfflineAudioContext synthesizers over-allocate for
// note release decays but never trim back.
// If the source is already ≤ targetDur (± 1 sample) the original buffer is
// returned unchanged so we don't burn CPU or lose precision on typical takes.
export function trimBufferTo(ctx, srcBuf, targetDur) {
  if (!srcBuf || targetDur <= 0) return srcBuf;
  const sr = srcBuf.sampleRate;
  // floor (not round) so the trimmed buffer is ALWAYS ≤ targetDur — even a
  // sub-millisecond overshoot flips `getBarCount`'s Math.ceil from 4 to 5,
  // which is exactly the "phantom 5th bar" bug the user reported.
  const targetSamples = Math.max(1, Math.floor(targetDur * sr));
  if (targetSamples >= srcBuf.length - 1) return srcBuf;
  const out = ctx.createBuffer(srcBuf.numberOfChannels, targetSamples, sr);
  for (let c = 0; c < srcBuf.numberOfChannels; c++) {
    out.getChannelData(c).set(srcBuf.getChannelData(c).subarray(0, targetSamples));
  }
  return out;
}


export function getPeaks(audioBuffer, samples = 200) {
  const channel = audioBuffer.getChannelData(0);
  const blockSize = Math.floor(channel.length / samples);
  const peaks = [];
  for (let i = 0; i < samples; i++) {
    let max = 0;
    const start = i * blockSize;
    for (let j = 0; j < blockSize; j++) {
      const v = Math.abs(channel[start + j] || 0);
      if (v > max) max = v;
    }
    peaks.push(max);
  }
  return peaks;
}

// ---------------- Onset detection ----------------
// Simple RMS-energy onset detector. Returns onsets as {time, energy, centroidHz}.
export function detectOnsets(audioBuffer, { hopMs = 10, minGapMs = 90, threshold = 1.6, withCentroid = true } = {}) {
  const sr = audioBuffer.sampleRate;
  const ch = audioBuffer.getChannelData(0);
  const hop = Math.floor(sr * hopMs / 1000);
  const win = hop * 2;
  const frames = Math.floor((ch.length - win) / hop);
  const rms = new Float32Array(frames);
  for (let i = 0; i < frames; i++) {
    let s = 0;
    const start = i * hop;
    for (let j = 0; j < win; j++) {
      const v = ch[start + j] || 0;
      s += v * v;
    }
    rms[i] = Math.sqrt(s / win);
  }
  // smooth + adaptive thresholding
  const avg = new Float32Array(frames);
  const windowFrames = Math.max(5, Math.floor(150 / hopMs));
  for (let i = 0; i < frames; i++) {
    let s = 0;
    let n = 0;
    for (let k = Math.max(0, i - windowFrames); k <= Math.min(frames - 1, i + windowFrames); k++) {
      s += rms[k]; n++;
    }
    avg[i] = s / n;
  }
  const minGapFrames = Math.floor(minGapMs / hopMs);
  const onsets = [];
  let lastOnset = -minGapFrames - 1;
  for (let i = 1; i < frames - 1; i++) {
    const above = rms[i] > avg[i] * threshold && rms[i] > 0.04;
    const rising = rms[i] > rms[i - 1] && rms[i] >= rms[i + 1];
    if (above && rising && i - lastOnset >= minGapFrames) {
      onsets.push({ time: (i * hop) / sr, energy: rms[i] });
      lastOnset = i;
    }
  }
  // Recover the very first note when the recording opens on an already-loud
  // transient (e.g. count-in ends, user's first hum/hit is right at t=0). The
  // main loop starts at i=1 and needs `rms[i] > rms[i-1]` to fire, so an attack
  // that peaks at frame 0 or that begins before the earliest local minimum is
  // invisible to it. If we find substantial energy before the first detected
  // onset, prepend a synthetic onset at the first frame that crossed the
  // energy floor — restores the melody/chord input's first note when users
  // convert with Instrument or Chordify.
  const floorGapFrames = Math.max(3, Math.floor(30 / hopMs));
  if (onsets.length > 0) {
    const firstOnsetFrame = Math.floor((onsets[0].time * sr) / hop);
    let firstAboveFloor = -1;
    for (let i = 0; i < Math.min(firstOnsetFrame, frames); i++) {
      if (rms[i] > 0.04) { firstAboveFloor = i; break; }
    }
    if (firstAboveFloor >= 0 && firstOnsetFrame - firstAboveFloor >= floorGapFrames) {
      onsets.unshift({ time: (firstAboveFloor * hop) / sr, energy: rms[firstAboveFloor] });
    }
  } else {
    // Rare: main loop found nothing at all but there IS audio (e.g. one
    // sustained hum). Fall back to the first frame above the noise floor.
    for (let i = 0; i < frames; i++) {
      if (rms[i] > 0.04) { onsets.push({ time: (i * hop) / sr, energy: rms[i] }); break; }
    }
  }
  // compute spectral features (centroid + low/mid band energy) per onset window.
  if (withCentroid) {
    for (const o of onsets) {
      const feat = spectralFeatures(audioBuffer, o.time, 0.04);
      o.centroidHz = feat.centroidHz;
      o.lowEnergy = feat.lowEnergy;
      o.midEnergy = feat.midEnergy;
    }
  }
  return onsets;
}

function nextPow2(n) {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

// Naive DFT magnitude for short windows is OK for ~1024-sample bursts
// Compute spectral centroid + low/mid band RMS in one FFT pass. Used by the
// onset classifier: a strong low-band signal is the giveaway for a real kick.
function spectralFeatures(audioBuffer, startTime, lenSec) {
  const sr = audioBuffer.sampleRate;
  const ch = audioBuffer.getChannelData(0);
  const start = Math.max(0, Math.floor(startTime * sr));
  const len = Math.min(ch.length - start, Math.floor(lenSec * sr));
  if (len < 32) return { centroidHz: 0, lowEnergy: 0, midEnergy: 0 };
  const N = nextPow2(len);
  const re = new Float32Array(N);
  const im = new Float32Array(N);
  for (let i = 0; i < len; i++) {
    const w = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (len - 1)));
    re[i] = (ch[start + i] || 0) * w;
  }
  fft(re, im);
  const half = N / 2;
  const lowMaxBin = Math.min(half, Math.floor((300 * N) / sr));  // 0–300 Hz
  const midMaxBin = Math.min(half, Math.floor((2000 * N) / sr)); // 300–2000 Hz
  let num = 0, den = 0, lowE = 0, midE = 0;
  for (let k = 1; k < half; k++) {
    const mag = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
    const f = (k * sr) / N;
    num += f * mag;
    den += mag;
    if (k < lowMaxBin) lowE += mag * mag;
    else if (k < midMaxBin) midE += mag * mag;
  }
  return {
    centroidHz: den > 0 ? num / den : 0,
    lowEnergy: Math.sqrt(lowE),
    midEnergy: Math.sqrt(midE),
  };
}

// In-place radix-2 iterative FFT
function fft(re, im) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wlr = Math.cos(ang);
    const wli = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let wr = 1, wi = 0;
      for (let k = 0; k < len / 2; k++) {
        const xr = re[i + k];
        const xi = im[i + k];
        const yr = re[i + k + len / 2] * wr - im[i + k + len / 2] * wi;
        const yi = re[i + k + len / 2] * wi + im[i + k + len / 2] * wr;
        re[i + k] = xr + yr;
        im[i + k] = xi + yi;
        re[i + k + len / 2] = xr - yr;
        im[i + k + len / 2] = xi - yi;
        const tr = wr * wlr - wi * wli;
        wi = wr * wli + wi * wlr;
        wr = tr;
      }
    }
  }
}

// Estimate BPM from onsets via inter-onset-interval histogram (40-200 BPM)
export function estimateBPM(onsets) {
  if (onsets.length < 3) return null;
  const intervals = [];
  for (let i = 1; i < onsets.length; i++) {
    intervals.push(onsets[i].time - onsets[i - 1].time);
  }
  // candidate BPMs from 60..200, find best by mod fit
  let bestBpm = null;
  let bestScore = -Infinity;
  for (let bpm = 60; bpm <= 200; bpm++) {
    const beat = 60 / bpm;
    let score = 0;
    for (const dt of intervals) {
      // distance of dt to nearest multiple of beat (allow eighths)
      const ratios = [beat, beat / 2, beat * 2];
      let best = Infinity;
      for (const r of ratios) {
        const m = Math.round(dt / r);
        if (m <= 0) continue;
        const err = Math.abs(dt - m * r);
        if (err < best) best = err;
      }
      score += Math.max(0, 1 - best / (beat * 0.25));
    }
    if (score > bestScore) {
      bestScore = score;
      bestBpm = bpm;
    }
  }
  return bestBpm;
}

// Classify onset → kick / snare / hat.
// Calibrated for voice input (beatboxing/humming), NOT clean drum-machine samples —
// vocal "kick" sounds have breath+consonant energy that pushes the spectral centroid
// up to 1.5–3 kHz, so tight drum-machine thresholds mis-label them as hats.
// Two-tier decision: a strong low-frequency energy signal ALWAYS wins (a real
// boom/kick), otherwise fall back to centroid bands with voice-friendly cutoffs.
export function classifyHit(o) {
  if (!o) return "kick";
  const c = o.centroidHz || 0;
  const lowE = o.lowEnergy || 0;   // sub-300-Hz RMS
  const midE = o.midEnergy || 0;   // 300-2000-Hz RMS
  // Strong low-frequency energy → kick, even if centroid says otherwise.
  if (lowE > 0 && lowE > midE * 1.3) return "kick";
  // Centroid-based fallback with voice-friendly boundaries.
  if (c < HIT_CENTROID_KICK_MAX) return "kick";
  if (c < HIT_CENTROID_SNARE_MAX) return "snare";
  return "hat";
}

// Convenience: run onset detection with centroid+energy data and classify each
// hit. Returns an array of "kick"|"snare"|"hat" whose indices are aligned with
// the same onset indices used by note-blocks, hit_overrides and Drumify.
// Used by TrackTimeline to show a dim "suggestion" letter over each note so
// users can see where the auto-classifier *would* route it before overriding.
export function classifierSuggestions(buffer) {
  if (!buffer) return [];
  try {
    const onsets = detectOnsets(buffer, { withCentroid: true });
    return onsets.map((o) => classifyHit(o));
  } catch {
    return [];
  }
}

// ------------- Mixer playback -------------
// Build a fixed-length buffer matching loopDuration, padded with silence after the source ends.
// Sample-accurate way to make Web Audio loop arbitrary-length sources at a shared boundary.
// Notes on precision:
//   • Output buffer uses `ctx.sampleRate` (not the source's) so the AudioBufferSourceNode
//     doesn't have to resample at play time — resampling shifts loopEnd by fractional
//     samples and truncates the tail, which is what caused the "last bar cut off" bug.
//   • Sample count uses `Math.round`, not `Math.floor`, so we don't systematically
//     lose up to one sample of the loop window on non-integer BPMs.
//   • For non-synced tracks whose source is shorter than the loop window we
//     upsample/downsample nearest-neighbour when rates differ (cheap; only used
//     when browsers hand us a decoded buffer at a rate that doesn't match the
//     playback context — rare but happens on Safari and older Firefox).
function buildLoopBuffer(ctx, srcBuf, loopDur, { tile = false } = {}) {
  const sr = ctx.sampleRate;
  // Ceil (not round or floor) so the loop window is never shorter than
  // requested. Worst case we add one sample (22.7 μs at 44.1 kHz) — inaudible,
  // but guarantees the last millisecond of bar 4 always plays.
  const totalSamples = Math.max(1, Math.ceil(loopDur * sr));
  const numCh = srcBuf.numberOfChannels;
  const out = ctx.createBuffer(numCh, totalSamples, sr);
  const rateScale = srcBuf.sampleRate / sr; // usually 1.0
  for (let c = 0; c < numCh; c++) {
    const srcData = srcBuf.getChannelData(c);
    const dstData = out.getChannelData(c);
    const srcLen = srcData.length;
    if (srcLen === 0) continue;
    if (tile) {
      // Synced tracks: repeat the (bar-aligned) source until the loop window is
      // full. Keeps every track playing on every cycle even if its own duration
      // is shorter than loopDuration.
      for (let i = 0; i < totalSamples; i++) {
        const srcIdx = rateScale === 1
          ? i % srcLen
          : Math.floor(i * rateScale) % srcLen;
        dstData[i] = srcData[srcIdx];
      }
    } else {
      // Non-synced tracks: single copy padded with silence so raw take timing
      // is preserved (a vocal recorded at bar 1 stays at bar 1 every loop).
      const effectiveSrcSamples = rateScale === 1 ? srcLen : Math.floor(srcLen / rateScale);
      const n = Math.min(effectiveSrcSamples, totalSamples);
      if (rateScale === 1) {
        for (let i = 0; i < n; i++) dstData[i] = srcData[i];
      } else {
        for (let i = 0; i < n; i++) dstData[i] = srcData[Math.floor(i * rateScale)];
      }
    }
  }
  return out;
}

// ------------- Per-track reverb -------------
// Instrument/chord ("melody" kind) tracks get a dry/wet convolution reverb.
// The impulse response is synthesized noise with an exponential decay —
// cached per AudioContext (WeakMap → offline contexts get GC'd with their IR).
// Reverb characters. Room = tight small space, Hall = long lush decay,
// Plate = shorter, brighter, metallic sheen (high-frequency pre-emphasis),
// Voice = tuned for vocals: 30ms pre-delay keeps words intelligible, damped
// highs (3-tap smoothing) for warmth, short-medium tail.
const REVERB_TYPES = {
  room:  { duration: 0.55, decay: 3.5 },
  hall:  { duration: 2.0,  decay: 2.8 },
  plate: { duration: 1.3,  decay: 1.8, bright: true },
  voice: { duration: 0.95, decay: 3.0, predelay: 0.03, damp: true },
};
// Per-track default character: vocals get the voice-tuned room, others hall.
export const reverbTypeFor = (t) => t?.reverb_type || (t?.kind === "vocal" ? "voice" : t?.kind === "drums" ? "room" : "hall");
const _irCache = new WeakMap();
function getImpulseResponse(ctx, type = "hall") {
  const spec = REVERB_TYPES[type] || REVERB_TYPES.hall;
  let byType = _irCache.get(ctx);
  if (!byType) { byType = {}; _irCache.set(ctx, byType); }
  if (byType[type]) return byType[type];
  const sr = ctx.sampleRate;
  const len = Math.floor(spec.duration * sr);
  const pre = Math.floor((spec.predelay || 0) * sr);
  const ir = ctx.createBuffer(2, len, sr);
  for (let c = 0; c < 2; c++) {
    const ch = ir.getChannelData(c);
    let p1 = 0;
    let p2 = 0;
    for (let i = pre; i < len; i++) {
      const k = (i - pre) / (len - pre);
      const raw = (Math.random() * 2 - 1) * Math.pow(1 - k, spec.decay);
      // Plate: first-difference tilt boosts highs. Voice: 3-tap average damps them.
      let s = raw;
      if (spec.bright) s = raw - 0.6 * p1;
      else if (spec.damp) s = (raw + p1 + p2) / 3;
      ch[i] = s;
      p2 = p1;
      p1 = raw;
    }
  }
  byType[type] = ir;
  return ir;
}

// Wires node → dest through a dry/wet split (amt 0..100; 100 = only reverb).
// Always builds the split so live slider moves from 0 work during playback.
// Returns the two gain nodes for realtime updates.
function attachReverbSplit(ctx, node, dest, amt, type = "hall") {
  const wetLevel = Math.max(0, Math.min(1, (amt || 0) / 100));
  const dry = ctx.createGain();
  dry.gain.value = 1 - wetLevel;
  const wet = ctx.createGain();
  wet.gain.value = wetLevel;
  const conv = ctx.createConvolver();
  conv.buffer = getImpulseResponse(ctx, type);
  node.connect(dry);
  dry.connect(dest);
  node.connect(conv);
  conv.connect(wet);
  wet.connect(dest);
  return { dry, wet, conv, type };
}

const hasReverbControl = (t) => t.kind === "melody" || t.kind === "vocal" || t.kind === "drums";

// ------------- Per-track drive (overdrive/distortion) -------------
// Melody (instrument/chord) tracks get a WaveShaper soft-clip stage between the
// volume gain and the reverb split. 0 = bit-perfect clean (identity curve),
// 100 = full tanh overdrive. Applied live during playback AND in mix/stem
// renders. Built for the Electric Guitar voice but works on any melody track.
const hasDriveControl = (t) => t.kind === "melody";

function makeDriveCurve(amt) {
  const a = Math.max(0, Math.min(100, amt || 0)) / 100;
  const n = 1024;
  const curve = new Float32Array(n);
  const k = a * 8;
  const norm = k > 0.01 ? Math.tanh(k) : 1;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = k > 0.01 ? Math.tanh(k * x) / norm : x;
  }
  return curve;
}

// Makeup gain keeps perceived loudness roughly level as drive compresses peaks.
const driveMakeup = (amt) => 1 / (1 + ((amt || 0) / 100) * 1.2);

function attachDrive(ctx, node, amt) {
  const shaper = ctx.createWaveShaper();
  shaper.curve = makeDriveCurve(amt);
  shaper.oversample = "4x";
  const makeup = ctx.createGain();
  makeup.gain.value = driveMakeup(amt);
  node.connect(shaper);
  shaper.connect(makeup);
  return { shaper, makeup, amt: amt || 0 };
}

export class MixerPlayer {
  constructor() {
    this.ctx = getAudioContext();
    this.sources = [];
    this.gains = [];
    // Map<trackId, GainNode> — enables O(1) per-track gain updates during
    // playback (see `updateTracks` below).
    this.trackGains = new Map();
    // Map<trackId, {dry, wet}> — per-track reverb splits for live updates.
    this.trackReverbs = new Map();
    // Map<trackId, {shaper, makeup, amt}> — per-track drive stages for live updates.
    this.trackDrives = new Map();
    this.startTime = 0;
    this.duration = 0;
    this.loopDuration = 0;
  }
  async play(tracks, { loopDuration = null } = {}) {
    await this.stop();
    if (this.ctx.state === "suspended") await this.ctx.resume();
    const hasSolo = tracks.some((t) => t.solo);
    const audibleTracks = tracks.filter((t) => t.buffer);
    if (audibleTracks.length === 0) return 0;

    const t0 = this.ctx.currentTime + 0.05;

    if (loopDuration && loopDuration > 0) {
      // Loop mode: every track plays inside a fixed `loopDuration` window and
      // loops forever until stop(). To keep every source phase-locked to the
      // shared boundary, we build a loopDuration-sized buffer for EACH track:
      //   • synced tracks → tile the (bar-aligned) source through the window
      //     so short sync clips still play every cycle without drifting.
      //   • non-synced tracks → single copy + silence padding so recorded
      //     timing is preserved (a vocal at bar 1 stays at bar 1 every loop).
      //   • any source longer than loopDuration is trimmed at the window edge
      //     — recordings are now hard-trimmed at capture time (see
      //     `trimBufferTo` in Studio.jsx / stopRecording) so this only kicks
      //     in for legacy takes uploaded before that fix.
      // loopEnd is set to 0 which Web Audio interprets as "loop at the natural
      // buffer end" — this is more precise than passing a seconds value that
      // gets floating-point rounded internally.
      let actualLoopDur = loopDuration;
      audibleTracks.forEach((t) => {
        const audible = hasSolo ? t.solo : !t.muted;
        const g = this.ctx.createGain();
        g.gain.value = audible ? t.volume : 0;
        let head = g;
        if (hasDriveControl(t)) {
          const d = attachDrive(this.ctx, g, t.drive);
          this.trackDrives.set(t.id, d);
          head = d.makeup;
        }
        if (hasReverbControl(t)) {
          this.trackReverbs.set(t.id, attachReverbSplit(this.ctx, head, this.ctx.destination, t.reverb, reverbTypeFor(t)));
        } else {
          head.connect(this.ctx.destination);
        }
        const src = this.ctx.createBufferSource();
        const looped = buildLoopBuffer(this.ctx, t.buffer, loopDuration, { tile: !!t.is_synced });
        src.buffer = looped;
        src.loop = true;
        src.loopStart = 0;
        src.loopEnd = 0; // 0 → Web Audio uses buffer.duration exactly
        actualLoopDur = looped.duration;
        src.connect(g);
        src.start(t0);
        this.sources.push(src);
        this.gains.push(g);
        this.trackGains.set(t.id, g);
      });
      this.startTime = t0;
      this.duration = actualLoopDur;
      this.loopDuration = actualLoopDur;
      return actualLoopDur;
    }

    // One-shot mode (kept for callers that don't pass loopDuration).
    // Synced tracks loop, so they don't define the timeline length.
    // Timeline = longest NON-synced track. If everything is synced, fall back to
    // 4 loops of the longest synced track (so loops are audible).
    const nonSyncedDur = Math.max(0, ...audibleTracks
      .filter((t) => !t.is_synced)
      .map((t) => t.buffer.duration));
    const syncedDur = Math.max(0, ...audibleTracks
      .filter((t) => t.is_synced)
      .map((t) => t.buffer.duration));
    const totalDur = nonSyncedDur > 0 ? nonSyncedDur : syncedDur * 4;
    if (totalDur <= 0) return 0;

    audibleTracks.forEach((t) => {
      const audible = hasSolo ? t.solo : !t.muted;
      const src = this.ctx.createBufferSource();
      src.buffer = t.buffer;
      const g = this.ctx.createGain();
      g.gain.value = audible ? t.volume : 0;
      src.connect(g);
      let head = g;
      if (hasDriveControl(t)) {
        const d = attachDrive(this.ctx, g, t.drive);
        this.trackDrives.set(t.id, d);
        head = d.makeup;
      }
      if (hasReverbControl(t)) {
        this.trackReverbs.set(t.id, attachReverbSplit(this.ctx, head, this.ctx.destination, t.reverb, reverbTypeFor(t)));
      } else {
        head.connect(this.ctx.destination);
      }
      if (t.is_synced) {
        src.loop = true;
        src.loopStart = 0;
        src.loopEnd = t.buffer.duration;
        src.start(t0);
        src.stop(t0 + totalDur);
      } else {
        src.start(t0);
      }
      this.sources.push(src);
      this.gains.push(g);
      this.trackGains.set(t.id, g);
    });
    this.startTime = t0;
    this.duration = totalDur;
    this.loopDuration = 0;
    return totalDur;
  }

  // Re-thread the gain of every currently-playing source based on the caller's
  // latest tracks state. Called by Studio.jsx on any mute / solo / volume /
  // add / remove change while `playing === true`. If a track is no longer
  // playing (e.g. deleted from the project), its gain is silenced but the
  // source keeps running until stop() — safe because playback state is
  // rebuilt on the next play().
  updateTracks(tracks) {
    if (this.trackGains.size === 0) return;
    const hasSolo = tracks.some((t) => t.solo);
    const knownIds = new Set();
    for (const t of tracks) {
      knownIds.add(t.id);
      const g = this.trackGains.get(t.id);
      if (!g) continue; // track added after play() — will be included on next play()
      const audible = hasSolo ? t.solo : !t.muted;
      // setValueAtTime avoids the audible zipper noise of a bare assignment
      // when the caller drags the volume slider quickly.
      g.gain.setValueAtTime(audible ? t.volume : 0, this.ctx.currentTime);
      const rv = this.trackReverbs.get(t.id);
      if (rv) {
        const wetLevel = Math.max(0, Math.min(1, (t.reverb || 0) / 100));
        rv.dry.gain.setValueAtTime(1 - wetLevel, this.ctx.currentTime);
        rv.wet.gain.setValueAtTime(wetLevel, this.ctx.currentTime);
        const wantType = reverbTypeFor(t);
        if (rv.type !== wantType) {
          rv.type = wantType;
          rv.conv.buffer = getImpulseResponse(this.ctx, wantType);
        }
      }
      const dv = this.trackDrives.get(t.id);
      if (dv && dv.amt !== (t.drive || 0)) {
        dv.amt = t.drive || 0;
        dv.shaper.curve = makeDriveCurve(dv.amt);
        dv.makeup.gain.setValueAtTime(driveMakeup(dv.amt), this.ctx.currentTime);
      }
    }
    // Silence any registered gain whose track was removed from the array.
    for (const [id, g] of this.trackGains) {
      if (!knownIds.has(id)) g.gain.setValueAtTime(0, this.ctx.currentTime);
    }
  }

  async stop() {
    this.sources.forEach((s) => {
      try {
        s.stop();
      } catch (err) {
        // Web Audio throws on double-stop when a source already ended. Not a real
        // failure — swallow but log at debug level so it's inspectable in dev.
        logger.debug("MixerPlayer.stop: source already stopped:", err?.message || err);
      }
    });
    this.sources = [];
    this.gains = [];
    this.trackGains.clear();
    this.trackReverbs.clear();
    this.trackDrives.clear();
    this.loopDuration = 0;
  }
  currentPosition() {
    if (!this.startTime) return 0;
    const elapsed = Math.max(0, this.ctx.currentTime - this.startTime);
    if (this.loopDuration > 0) return elapsed % this.loopDuration;
    return elapsed;
  }
}

// ------------- Render mix -------------
async function renderMixToBuffer(tracks) {
  const hasSolo = tracks.some((t) => t.solo);
  const audible = tracks.filter((t) => t.buffer && (hasSolo ? t.solo : !t.muted));
  if (audible.length === 0) return null;
  const sampleRate = SAMPLE_RATE;
  const nonSyncedDur = Math.max(0, ...audible.filter((t) => !t.is_synced).map((t) => t.buffer.duration));
  const syncedDur = Math.max(0, ...audible.filter((t) => t.is_synced).map((t) => t.buffer.duration));
  const duration = nonSyncedDur > 0 ? nonSyncedDur : syncedDur * 4;
  if (duration <= 0) return null;
  const offline = new OfflineAudioContext(2, Math.ceil(duration * sampleRate), sampleRate);
  audible.forEach((t) => {
    const src = offline.createBufferSource();
    src.buffer = t.buffer;
    const g = offline.createGain();
    g.gain.value = t.volume;
    src.connect(g);
    let head = g;
    if (hasDriveControl(t) && (t.drive || 0) > 0) {
      head = attachDrive(offline, g, t.drive).makeup;
    }
    if (hasReverbControl(t) && (t.reverb || 0) > 0) {
      attachReverbSplit(offline, head, offline.destination, t.reverb, reverbTypeFor(t));
    } else {
      head.connect(offline.destination);
    }
    if (t.is_synced) {
      src.loop = true;
      src.loopStart = 0;
      src.loopEnd = t.buffer.duration;
      src.start(0);
      src.stop(duration);
    } else {
      src.start(0);
    }
  });
  return await offline.startRendering();
}

export async function renderMixToWav(tracks) {
  const rendered = await renderMixToBuffer(tracks);
  if (!rendered) return null;
  return audioBufferToWavBlob(rendered);
}

export async function renderMixToMp3(tracks, bitrate = 192) {
  const rendered = await renderMixToBuffer(tracks);
  if (!rendered) return null;
  return audioBufferToMp3Blob(rendered, bitrate);
}

// Render ONE track in isolation (same source setup as the mix render: volume
// applied, synced loops repeated) to a WAV or MP3 blob. `duration` is shared
// across all stems in an export so they line up sample-accurately in any DAW.
export async function renderStemToBlob(track, duration, format = "wav", bitrate = 192) {
  if (!track?.buffer || duration <= 0) return null;
  const offline = new OfflineAudioContext(2, Math.ceil(duration * SAMPLE_RATE), SAMPLE_RATE);
  const src = offline.createBufferSource();
  src.buffer = track.buffer;
  const g = offline.createGain();
  g.gain.value = track.volume ?? 1;
  src.connect(g);
  let head = g;
  if (hasDriveControl(track) && (track.drive || 0) > 0) {
    head = attachDrive(offline, g, track.drive).makeup;
  }
  if (hasReverbControl(track) && (track.reverb || 0) > 0) {
    attachReverbSplit(offline, head, offline.destination, track.reverb, reverbTypeFor(track));
  } else {
    head.connect(offline.destination);
  }
  if (track.is_synced) {
    src.loop = true;
    src.loopStart = 0;
    src.loopEnd = track.buffer.duration;
    src.start(0);
    src.stop(duration);
  } else {
    src.start(0);
  }
  const rendered = await offline.startRendering();
  return format === "mp3" ? audioBufferToMp3Blob(rendered, bitrate) : audioBufferToWavBlob(rendered);
}

function audioBufferToWavBlob(buffer) {
  const numCh = buffer.numberOfChannels;
  const length = buffer.length * numCh * 2 + 44;
  const ab = new ArrayBuffer(length);
  const view = new DataView(ab);
  const channels = [];
  let offset = 0;
  let pos = 0;
  function setUint16(v) { view.setUint16(pos, v, true); pos += 2; }
  function setUint32(v) { view.setUint32(pos, v, true); pos += 4; }
  setUint32(0x46464952); setUint32(length - 8); setUint32(0x45564157);
  setUint32(0x20746d66); setUint32(16); setUint16(1); setUint16(numCh);
  setUint32(buffer.sampleRate); setUint32(buffer.sampleRate * numCh * 2);
  setUint16(numCh * 2); setUint16(16); setUint32(0x61746164); setUint32(length - pos - 4);
  for (let i = 0; i < numCh; i++) channels.push(buffer.getChannelData(i));
  while (pos < length) {
    for (let i = 0; i < numCh; i++) {
      let s = Math.max(-1, Math.min(1, channels[i][offset]));
      s = s < 0 ? s * 0x8000 : s * 0x7fff;
      view.setInt16(pos, s, true);
      pos += 2;
    }
    offset++;
  }
  return new Blob([ab], { type: "audio/wav" });
}

function floatTo16(arr) {
  const out = new Int16Array(arr.length);
  for (let i = 0; i < arr.length; i++) {
    let s = Math.max(-1, Math.min(1, arr[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function audioBufferToMp3Blob(buffer, bitrate = 192) {
  const sr = buffer.sampleRate;
  const numCh = Math.min(2, buffer.numberOfChannels);
  const left = floatTo16(buffer.getChannelData(0));
  const right = numCh > 1 ? floatTo16(buffer.getChannelData(1)) : null;
  const encoder = new lamejs.Mp3Encoder(numCh, sr, bitrate);
  const blockSize = 1152;
  const chunks = [];
  for (let i = 0; i < left.length; i += blockSize) {
    const l = left.subarray(i, i + blockSize);
    const r = right ? right.subarray(i, i + blockSize) : null;
    const mp3buf = numCh > 1 ? encoder.encodeBuffer(l, r) : encoder.encodeBuffer(l);
    if (mp3buf.length > 0) chunks.push(new Int8Array(mp3buf));
  }
  const end = encoder.flush();
  if (end.length > 0) chunks.push(new Int8Array(end));
  return new Blob(chunks, { type: "audio/mp3" });
}

// ------------- Count-in metronome -------------
// Plays a `beats`-quarter-note click train at the given BPM.
// Resolves at the AudioContext-aligned "next 1" — so the caller can start MediaRecorder
// EXACTLY on the downbeat following the count-in, with no JS-clock drift.
// onTick(beatNumber) fires when each click is audible (beatNumber is 1..beats).
export async function playCountIn(bpm, beats = 4, onTick) {
  const ctx = getAudioContext();
  if (ctx.state === "suspended") await ctx.resume();
  const beatSec = 60 / Math.max(40, Math.min(240, bpm || 120));
  const LEAD_IN = 0.06; // sec — Web Audio scheduling head-room
  const audioStart = ctx.currentTime + LEAD_IN;

  for (let i = 0; i < beats; i++) {
    const t = audioStart + i * beatSec;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    // Downbeat (beat 1) gets a higher pitch for emphasis
    osc.frequency.setValueAtTime(i === 0 ? 1500 : 880, t);
    osc.type = "square";
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(0.35, t + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.08);
  }

  if (onTick) {
    for (let i = 0; i < beats; i++) {
      const delayMs = Math.max(0, (audioStart + i * beatSec - ctx.currentTime) * 1000);
      setTimeout(() => onTick(i + 1), delayMs);
    }
  }

  // Wait until the AudioContext-aligned "next 1" so MediaRecorder starts on the downbeat.
  // (Math: audioStart + beats*beatSec is the moment AFTER the final click, i.e. the next bar's "1".)
  const nextDownbeat = audioStart + beats * beatSec;
  const coarseWaitMs = Math.max(0, (nextDownbeat - ctx.currentTime) * 1000 - 8);
  if (coarseWaitMs > 0) await new Promise((r) => setTimeout(r, coarseWaitMs));
  // Spin-wait the last <8 ms so setTimeout jitter doesn't chop the first hit off
  // the take. AudioContext.currentTime advances at sample-clock precision.
  while (ctx.currentTime < nextDownbeat) { /* busy-wait */ }
}

// ------------- Quantize / Sync to BPM grid -------------
// Slices `srcBuffer` at each onset; re-places each slice at the nearest grid position
// for the given BPM + division (e.g. 16 → 1/16th note grid). Non-destructive: returns
// a NEW AudioBuffer. Each slice gets a short fade in/out to avoid clicks.
export async function quantizeAudioToGrid(srcBuffer, onsets, bpm, division = 16) {
  if (!srcBuffer || !onsets || onsets.length === 0) return null;
  const sampleRate = srcBuffer.sampleRate;
  const numCh = srcBuffer.numberOfChannels;
  const beatSec = 60 / bpm;
  // division=4 → quarter note (beatSec); 8 → 8th (beatSec/2); 16 → 16th (beatSec/4); 32 → 32nd (beatSec/8)
  const gridStep = beatSec * (4 / division);

  // Compute slices [start, end) for each onset (last slice goes to end of buffer)
  const slices = onsets.map((o, i) => {
    const start = o.time;
    const end = i + 1 < onsets.length ? onsets[i + 1].time : srcBuffer.duration;
    const quantizedStart = Math.max(0, Math.round(start / gridStep) * gridStep);
    return { start, end, quantizedStart };
  });

  // Buffer length: enough to fit the latest quantized slice's end
  const lastEnd = Math.max(
    ...slices.map((s) => s.quantizedStart + (s.end - s.start)),
    srcBuffer.duration
  );
  const outLen = Math.ceil(lastEnd * sampleRate) + sampleRate; // 1 sec tail
  const offline = new OfflineAudioContext(numCh, outLen, sampleRate);

  for (const sl of slices) {
    const sliceLenSec = sl.end - sl.start;
    if (sliceLenSec <= 0) continue;
    const sliceLen = Math.floor(sliceLenSec * sampleRate);
    const sliceBuf = offline.createBuffer(numCh, sliceLen, sampleRate);
    const fadeLen = Math.min(220, Math.floor(sliceLen / 6));
    for (let ch = 0; ch < numCh; ch++) {
      const srcData = srcBuffer.getChannelData(ch);
      const dstData = sliceBuf.getChannelData(ch);
      const srcOffset = Math.floor(sl.start * sampleRate);
      for (let j = 0; j < sliceLen; j++) {
        let v = srcData[srcOffset + j] || 0;
        if (j < fadeLen) v *= j / fadeLen;
        else if (j >= sliceLen - fadeLen) v *= (sliceLen - 1 - j) / fadeLen;
        dstData[j] = v;
      }
    }
    const src = offline.createBufferSource();
    src.buffer = sliceBuf;
    src.connect(offline.destination);
    src.start(sl.quantizedStart);
  }

  return await offline.startRendering();
}

// Convenience: detect onsets + quantize in one call
export async function syncAudioToBpm(srcBuffer, bpm, division = 16) {
  const onsets = detectOnsets(srcBuffer);
  if (onsets.length === 0) return null;
  const buf = await quantizeAudioToGrid(srcBuffer, onsets, bpm, division);
  return { buffer: buf, onsetCount: onsets.length };
}

// ------------- Master tempo re-time (BPM change) -------------
// Pitch-PRESERVING time-stretch via SoundTouch WSOLA. tempo = newBpm/oldBpm:
// >1 plays faster (shorter audio), <1 slower (longer). The source is padded
// with silence so the WSOLA pipeline flushes its ~0.14 s latency tail, then the
// output is trimmed to exactly round(length / tempo) samples so loop math stays
// sample-accurate at the new BPM.
export async function stretchBufferToTempo(buffer, tempo) {
  if (!buffer || !(tempo > 0) || Math.abs(tempo - 1) < 0.0005) return buffer;
  const { SoundTouch, SimpleFilter } = await import("soundtouchjs");
  const sr = buffer.sampleRate;
  const n = buffer.length;
  const numCh = buffer.numberOfChannels;
  const l = buffer.getChannelData(0);
  const r = numCh > 1 ? buffer.getChannelData(1) : l;
  const pad = Math.floor(0.5 * sr);
  const source = {
    extract(target, numFrames, position) {
      let count = 0;
      for (let i = 0; i < numFrames; i++) {
        const idx = position + i;
        if (idx >= n + pad) break;
        const vl = idx < n ? l[idx] : 0;
        const vr = idx < n ? r[idx] : 0;
        target[i * 2] = vl;
        target[i * 2 + 1] = vr;
        count++;
      }
      return count;
    },
  };
  const st = new SoundTouch();
  st.tempo = tempo;
  const filter = new SimpleFilter(source, st);
  const targetLen = Math.round(n / tempo);
  const outL = new Float32Array(targetLen);
  const outR = numCh > 1 ? new Float32Array(targetLen) : null;
  const CHUNK = 8192;
  const tmp = new Float32Array(CHUNK * 2);
  let written = 0;
  while (written < targetLen) {
    const frames = filter.extract(tmp, CHUNK);
    if (frames <= 0) break;
    const take = Math.min(frames, targetLen - written);
    for (let i = 0; i < take; i++) {
      outL[written + i] = tmp[i * 2];
      if (outR) outR[written + i] = tmp[i * 2 + 1];
    }
    written += take;
  }
  const out = getAudioContext().createBuffer(numCh, targetLen, sr);
  out.getChannelData(0).set(outL);
  if (outR) out.getChannelData(1).set(outR);
  return out;
}

// ------------- Drum synthesis from onsets -------------
// Per-kit voicing parameters. Each kit reshapes pitch envelopes and decays.
// Kit registry. `lock` (if set) forces every onset in that kit to a single
// voicing — kicks-only for the vintage electronic kits, hats-only for the
// Hi-Hat top-line kit, snares-only for the Snare kit. Kits without a lock use
// full 3-way classifyHit routing.
export const DRUM_KITS = [
  { v: "808", l: "TR-808", lock: "kick" },
  { v: "909", l: "TR-909", lock: "kick" },
  // Hi-Hat kit re-uses /samples/acoustic/hat.wav — there is no real
  // /samples/hat-only/ folder. Aliasing is handled in loadKit below.
  { v: "hat-only", l: "Hi-Hat", lock: "hat" },
  // Snare kit re-uses /samples/acoustic/snare.wav for a dry backbeat.
  { v: "snare-only", l: "Snare", lock: "snare" },
  // Cymbal kits load from /samples/cymbals/{crash|ride}.wav (see loadKit).
  { v: "crash-only", l: "Crash Cymbal", lock: "crash" },
  { v: "ride-only", l: "Ride Cymbal", lock: "ride" },
  { v: "snap-only", l: "Finger Snaps", lock: "snap" },
  { v: "acoustic", l: "Acoustic" },
  { v: "lofi", l: "Lo-fi" },
];

function voicingLockFor(kit) {
  const entry = DRUM_KITS.find((k) => k.v === kit);
  return entry?.lock || null;
}

// In-memory cache for decoded drum samples. Keyed by `{kit}/{drum}` → AudioBuffer.
// Samples live at /samples/{kit}/{kick|snare|hat}.wav (pre-baked offline; see
// /app/scripts/generate_drums.py).
const SAMPLE_CACHE = new Map();

async function loadSample(kit, drum) {
  const key = `${kit}/${drum}`;
  if (SAMPLE_CACHE.has(key)) return SAMPLE_CACHE.get(key);
  const ctx = getAudioContext();
  const resp = await fetch(`/samples/${kit}/${drum}.wav`);
  if (!resp.ok) throw new Error(`sample 404: ${key}`);
  const arr = await resp.arrayBuffer();
  const buf = await ctx.decodeAudioData(arr);
  SAMPLE_CACHE.set(key, buf);
  return buf;
}

async function loadKit(kit) {
  const lock = voicingLockFor(kit);
  if (lock === "kick") {
    // TR-808 / TR-909 are kick-only voicings — skip snare/hat fetches entirely.
    const kick = await loadSample(kit, "kick");
    return { kick };
  }
  if (lock === "hat") {
    // Hi-Hat kit uses the acoustic hat sample for a dry, real-drum-kit sizzle.
    const hat = await loadSample("acoustic", "hat");
    return { hat };
  }
  if (lock === "snare") {
    // Snare kit uses the acoustic snare sample for a dry backbeat.
    const snare = await loadSample("acoustic", "snare");
    return { snare };
  }
  if (lock === "crash") {
    const crash = await loadSample("cymbals", "crash");
    return { crash };
  }
  if (lock === "ride") {
    const ride = await loadSample("cymbals", "ride");
    return { ride };
  }
  if (lock === "snap") {
    const snap = await loadSample("snaps", "snap");
    return { snap };
  }
  const [kick, snare, hat] = await Promise.all([
    loadSample(kit, "kick"),
    loadSample(kit, "snare"),
    loadSample(kit, "hat"),
  ]);
  return { kick, snare, hat };
}

// Trigger a pre-rendered sample at `time` with energy-based gain. Used by every kit.
// `voicing` ('kick' | 'snare' | 'hat' | null) unlocks velocity-swing on kicks —
// louder vocal onsets drive a punchier, slightly deeper kick, matching how real
// drums behave (harder hit = more shell resonance).
function triggerSample(offline, sampleBuf, time, energy = 0.5, out = offline.destination, voicing = null) {
  const src = offline.createBufferSource();
  src.buffer = sampleBuf;
  const g = offline.createGain();
  if (voicing === "kick") {
    const clamped = Math.max(0, Math.min(1, energy));
    // Wider dynamic range on kicks: soft hits are quieter, hard hits punch harder.
    g.gain.value = Math.min(1.18, 0.42 + clamped * 1.3);
    // ~8 % pitch swing — heavier onsets play back slightly slower (deeper thud).
    src.playbackRate.value = 1.03 - clamped * 0.09;
  } else {
    // Non-kick voicings keep the original loudness curve so acoustic/lofi mixes
    // don't shift underneath the user.
    g.gain.value = Math.min(1.05, 0.5 + energy * 1.1);
  }
  src.connect(g).connect(out);
  src.start(time);
}

// Render a new AudioBuffer by triggering the chosen kit's WAV samples at each
// detected onset time.
// - TR-808 / TR-909 → kick-only voicing (per-user preference; the vintage
//   electronic kits should thump, not tick).
// - Hi-Hat → hat-only voicing (every onset plays the acoustic hi-hat, ideal for
//   laying a top-line hat groove over an already-drumified kick).
// - Acoustic / Lo-fi → full 3-way classification (kick / snare / hat) via
//   spectral centroid, matching the original behavior.
//
// `overrides` (optional): { [onsetIndex: number]: 'kick'|'snare'|'hat'|'crash' } —
// lets the user hand-reclassify individual hits from the timeline. kick/snare/hat
// overrides are only honored on kits without a hard `lock` (i.e. Acoustic and
// Lo-fi); locked kits ignore them so the user's kit choice remains the source of
// truth. 'crash' is the exception: a crash ACCENT cuts through any kit (including
// locked ones) — it exists precisely to mark big transitions inside a groove.
export async function synthesizeDrumsFromOnsets(srcBuffer, onsets, bpm = 120, kit = "808", overrides = null) {
  const sampleRate = SAMPLE_RATE;
  const duration = srcBuffer.duration;
  const offline = new OfflineAudioContext(1, Math.ceil(duration * sampleRate) + sampleRate, sampleRate);
  const resolvedKit = DRUM_KITS.find((k) => k.v === kit) ? kit : "808";
  const lock = voicingLockFor(resolvedKit);
  const samples = await loadKit(resolvedKit);
  if (overrides && Object.values(overrides).includes("crash")) {
    samples.crash = await loadSample("cymbals", "crash");
  }
  for (let i = 0; i < onsets.length; i++) {
    const o = onsets[i];
    // Precedence: crash override > kit lock > user override > centroid classifyHit.
    const ovr = overrides ? (overrides[i] ?? overrides[String(i)]) : null;
    let kind = null;
    if (ovr === "crash") kind = "crash";
    else if (lock) kind = lock;
    else if (ovr === "kick" || ovr === "snare" || ovr === "hat") kind = ovr;
    if (!kind) kind = classifyHit(o);
    let buf = samples[kind];
    if (!buf) {
      // Missing sample for this classified kind — fall back to whatever the kit
      // does have so the mix still plays. Log a warning (dev-only) so a real
      // 404 on any voicing asset doesn't silently regress.
      logger.warn(`drum sample missing for ${resolvedKit}/${kind}, falling back`);
      buf = samples.kick || samples.hat || samples.snare;
    }
    triggerSample(offline, buf, o.time, o.energy, offline.destination, kind);
  }
  const rendered = await offline.startRendering();
  return trimBufferTo(getAudioContext(), rendered, duration);
}

// ------------- Bar editing (insert silence, delete bars) -------------
export function getBarSeconds(bpm, beatsPerBar = 4) {
  return beatsPerBar * (60 / Math.max(40, Math.min(240, bpm || 120)));
}

export function getBarCount(durationSec, bpm, beatsPerBar = 4) {
  return Math.max(1, Math.ceil(durationSec / getBarSeconds(bpm, beatsPerBar)));
}

// editPlan: array of entries describing the FINAL bar sequence.
//   { type: 'bar', sourceIndex: 0..N-1 }  → copy bar `sourceIndex` from srcBuffer
//   { type: 'silence' }                    → insert one bar of silence
// Returns a NEW AudioBuffer. Non-destructive.
export async function applyBarEdits(srcBuffer, bpm, editPlan, beatsPerBar = 4) {
  if (!srcBuffer || !editPlan || editPlan.length === 0) return null;
  const sampleRate = srcBuffer.sampleRate;
  const numCh = srcBuffer.numberOfChannels;
  const barSec = getBarSeconds(bpm, beatsPerBar);
  const barSamples = Math.floor(barSec * sampleRate);
  if (barSamples <= 0) return null;
  const totalSamples = barSamples * editPlan.length;
  const offline = new OfflineAudioContext(numCh, totalSamples, sampleRate);
  editPlan.forEach((entry, dstBar) => {
    if (entry.type !== "bar" || entry.sourceIndex == null) return; // silence = leave zeros
    const srcStart = entry.sourceIndex * barSamples;
    if (srcStart >= srcBuffer.length) return;
    const remaining = srcBuffer.length - srcStart;
    const copyLen = Math.min(barSamples, remaining);
    const sliceBuf = offline.createBuffer(numCh, copyLen, sampleRate);
    const fadeLen = Math.min(440, Math.floor(copyLen / 8));
    for (let ch = 0; ch < numCh; ch++) {
      const srcData = srcBuffer.getChannelData(ch);
      const dstData = sliceBuf.getChannelData(ch);
      for (let j = 0; j < copyLen; j++) {
        let v = srcData[srcStart + j] || 0;
        if (j < fadeLen) v *= j / fadeLen;
        else if (j >= copyLen - fadeLen) v *= (copyLen - 1 - j) / fadeLen;
        dstData[j] = v;
      }
    }
    const src = offline.createBufferSource();
    src.buffer = sliceBuf;
    src.connect(offline.destination);
    src.start(dstBar * barSec);
  });
  return await offline.startRendering();
}

// ------------- Monophonic pitch detection (autocorrelation) -------------
function autocorrelatePitch(channel, sampleRate, startSample, windowLen) {
  const end = Math.min(channel.length, startSample + windowLen);
  const actualLen = end - startSample;
  if (actualLen < 256) return null;
  let rms = 0;
  for (let i = 0; i < actualLen; i++) {
    const v = channel[startSample + i] || 0;
    rms += v * v;
  }
  rms = Math.sqrt(rms / actualLen);
  if (rms < 0.02) return null; // unvoiced / silent
  const minPeriod = Math.floor(sampleRate / 1000); // up to 1 kHz
  const maxPeriod = Math.floor(sampleRate / 80);   // down to 80 Hz
  let bestPeriod = -1;
  let bestCorr = 0;
  for (let p = minPeriod; p <= maxPeriod && p < actualLen / 2; p++) {
    let corr = 0;
    for (let i = 0; i + p < actualLen; i++) {
      corr += (channel[startSample + i] || 0) * (channel[startSample + i + p] || 0);
    }
    if (corr > bestCorr) {
      bestCorr = corr;
      bestPeriod = p;
    }
  }
  if (bestPeriod < 0) return null;
  return sampleRate / bestPeriod;
}

// Measure how long a sung note ACTUALLY lasts, instead of assuming it fills the
// gap until the next onset. Tracks the RMS envelope from the onset: the note
// ends where energy stays below ~15% of its peak for 3 consecutive 10 ms hops.
// This is what keeps a short staccato "da!" short instead of stretching it into
// a sustained tone (user-reported accuracy bug).
export function measureSungDuration(channel, sampleRate, startTime, maxEndTime) {
  const hop = Math.floor(0.01 * sampleRate);
  const startIdx = Math.floor(startTime * sampleRate);
  const endIdx = Math.min(channel.length, Math.floor(maxEndTime * sampleRate));
  const frames = Math.max(1, Math.floor((endIdx - startIdx) / hop));
  let peak = 0;
  const rms = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let sum = 0;
    const base = startIdx + f * hop;
    for (let i = 0; i < hop; i++) {
      const s = channel[base + i] || 0;
      sum += s * s;
    }
    rms[f] = Math.sqrt(sum / hop);
    if (rms[f] > peak) peak = rms[f];
  }
  if (peak < 1e-4) return maxEndTime - startTime;
  const floor = Math.max(0.15 * peak, 0.012);
  let below = 0;
  for (let f = 0; f < frames; f++) {
    if (rms[f] < floor) {
      below += 1;
      if (below >= 3) {
        const endFrame = f - below + 1;
        return Math.max(0.06, (endFrame * hop) / sampleRate);
      }
    } else {
      below = 0;
    }
  }
  return maxEndTime - startTime;
}

export function detectMelodyNotes(audioBuffer, onsets, noteLengths = null) {
  if (!audioBuffer || !onsets) return [];
  const sampleRate = audioBuffer.sampleRate;
  const channel = audioBuffer.getChannelData(0);
  const windowLen = Math.floor(0.08 * sampleRate);
  const notes = [];
  for (let i = 0; i < onsets.length; i++) {
    const start = onsets[i].time;
    const end = i + 1 < onsets.length ? onsets[i + 1].time : audioBuffer.duration;
    // Length precedence: user-dragged block length → measured sung length
    // (energy decay), both capped by the gap to the next onset.
    const explicit = noteLengths ? (noteLengths[i] ?? noteLengths[String(i)]) : null;
    const duration = typeof explicit === "number"
      ? Math.min(explicit, end - start)
      : measureSungDuration(channel, sampleRate, start, end);
    if (duration < 0.04) continue;
    const startSample = Math.floor(start * sampleRate);
    const freq = autocorrelatePitch(channel, sampleRate, startSample, windowLen);
    if (freq && freq >= 80 && freq <= 1000) {
      notes.push({ time: start, duration, freq, energy: onsets[i].energy });
    }
  }
  return notes;
}

// ------------- Instrument synthesis -------------
export const INSTRUMENTS = [
  { v: "piano",       l: "Piano" },
  { v: "rhodes",      l: "Rhodes" },
  { v: "synth",       l: "Synth" },
  { v: "strings",     l: "Strings" },
  { v: "violin",      l: "Violin" },
  { v: "horns",       l: "Horns" },
  { v: "symphony",    l: "Symphony" },
  { v: "guitar",      l: "Guitar" },
  { v: "bass-guitar", l: "Bass Guitar" },
];

// Chordify's menu differs from Instruments: Guitar is the user's real strummed
// blend (one-shot chord sample) plus a crunchy Electric Guitar (both
// user-supplied recordings), and Bass Guitar is swapped for Bass Synth.
export const CHORD_INSTRUMENTS = INSTRUMENTS.flatMap((i) => {
  if (i.v === "guitar") return [{ v: "strum-guitar", l: "Guitar" }, { v: "electric-guitar", l: "Electric Guitar" }];
  if (i.v === "bass-guitar") return [{ v: "bass-synth", l: "Bass Synth" }];
  return [i];
});

// Each instrument is now a pre-baked WAV at a fixed root pitch. At play time
// the sample is loaded once (cached in INSTRUMENT_SAMPLE_CACHE) and pitch-shifted
// via AudioBufferSourceNode.playbackRate to reach the target note frequency.
// This replaces the previous single-oscillator subtractive voices which sounded
// "digital / monotone" — the new samples were baked with real physical models
// (Karplus-Strong for plucked strings, additive-with-stiffness for piano, FM
// for the sub bass, dual detuned saw + biquad LPF for the synth pad).
// `rootHz` sets the sample's baked root frequency; `octShift` transposes the
// detected melody line by whole octaves for the bass instruments.
const INSTRUMENT_VOICES = {
  piano:         { rootHz: 220.0, samplePath: "/samples/instruments/piano.wav",       octShift:  0, attack: 0.005, release: 0.20 },
  rhodes:        { rootHz: 220.0, samplePath: "/samples/instruments/rhodes.wav",      octShift:  0, attack: 0.005, release: 0.25 },
  synth:         { rootHz: 440.0, samplePath: "/samples/instruments/synth.wav",       octShift:  0, attack: 0.020, release: 0.25 },
  strings:       { rootHz: 220.0, samplePath: "/samples/instruments/strings.wav",     octShift:  0, attack: 0.080, release: 0.45 },
  violin:        { rootHz: 440.0, samplePath: "/samples/instruments/violin.wav",      octShift:  0, attack: 0.060, release: 0.35 },
  horns:         { rootHz: 220.0, samplePath: "/samples/instruments/horns.wav",       octShift:  0, attack: 0.050, release: 0.30 },
  symphony:      { rootHz: 220.0, samplePath: "/samples/instruments/symphony.wav",    octShift:  0, attack: 0.090, release: 0.50 },
  guitar:        { rootHz: 220.0, samplePath: "/samples/instruments/guitar.wav",      octShift:  0, attack: 0.005, release: 0.20 },
  // User-supplied real electric guitar sample (root E2 = 82.4 Hz, see
  // /app/scripts/prepare_user_eguitar.py). octShift -1 keeps chord voicings in
  // the natural E2-E4 rhythm-guitar register (playbackRate ~1.6-3.6).
  "electric-guitar": { rootHz: 82.4, samplePath: "/samples/instruments/electric-guitar.wav?v=3", octShift: -1, attack: 0.004, release: 0.18 },
  // User-supplied strummed open-string blend (A2-D3-E3-G3-B3-E4; bass anchor
  // tuned to A2 = 110 Hz, see /app/scripts/prepare_strum_guitar.py).
  // `chordSample`: the sample IS the chord — synthesizeChords plays ONE pitched
  // instance per detected chord instead of stacking triad voices.
  "strum-guitar": { rootHz: 110.0, samplePath: "/samples/instruments/strum-guitar.wav", octShift: 0, attack: 0.004, release: 0.25, chordSample: true },
  "bass-guitar": { rootHz: 110.0, samplePath: "/samples/instruments/bass-guitar.wav", octShift: -1, attack: 0.005, release: 0.25 },
  "bass-synth":  { rootHz:  55.0, samplePath: "/samples/instruments/bass-synth.wav?v=2", octShift: -2, attack: 0.010, release: 0.30 },
};

const INSTRUMENT_SAMPLE_CACHE = new Map();

async function loadInstrumentSample(instrument) {
  const voice = INSTRUMENT_VOICES[instrument] || INSTRUMENT_VOICES.piano;
  if (INSTRUMENT_SAMPLE_CACHE.has(voice.samplePath)) return { voice, buffer: INSTRUMENT_SAMPLE_CACHE.get(voice.samplePath) };
  const ctx = getAudioContext();
  const resp = await fetch(voice.samplePath);
  if (!resp.ok) throw new Error(`instrument sample 404: ${voice.samplePath}`);
  const arr = await resp.arrayBuffer();
  const buffer = await ctx.decodeAudioData(arr);
  INSTRUMENT_SAMPLE_CACHE.set(voice.samplePath, buffer);
  return { voice, buffer };
}

// Schedule one pitched sample voice on an OfflineAudioContext. Used by both
// synthesizeMelody (monophonic) and synthesizeChords (polyphonic — 3-4 voices
// per onset, ampScale=1/sqrt(N) to avoid clipping).
function scheduleSampleVoice(offline, sampleBuf, freq, voice, t, noteDur, energy, ampScale = 1.0) {
  const targetFreq = freq * Math.pow(2, voice.octShift);
  const src = offline.createBufferSource();
  src.buffer = sampleBuf;
  src.playbackRate.value = targetFreq / voice.rootHz;
  const gain = offline.createGain();
  const peak = Math.min(0.85, 0.35 + (energy || 0.3) * 0.9) * ampScale;
  // Fast attack + explicit release ramp so the sample's own natural tail is
  // cut cleanly at the end of the note window.
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.linearRampToValueAtTime(peak, t + voice.attack);
  gain.gain.setValueAtTime(peak, t + noteDur);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + noteDur + voice.release);
  src.connect(gain).connect(offline.destination);
  src.start(t);
  // Stop enough beyond the release ramp so envelope doesn't clip mid-fade.
  src.stop(t + noteDur + voice.release + 0.05);
}

export async function synthesizeMelody(audioBuffer, notes, instrument = "piano") {
  if (!notes || notes.length === 0) return null;
  const { voice, buffer: sampleBuf } = await loadInstrumentSample(instrument);
  const sampleRate = SAMPLE_RATE;
  const duration = audioBuffer.duration;
  const offline = new OfflineAudioContext(1, Math.ceil(duration * sampleRate) + sampleRate, sampleRate);
  for (const n of notes) {
    const t = n.time;
    const noteDur = Math.max(0.1, Math.min(n.duration, 1.5));
    scheduleSampleVoice(offline, sampleBuf, n.freq, voice, t, noteDur, n.energy, 1.0);
  }
  const rendered = await offline.startRendering();
  return trimBufferTo(getAudioContext(), rendered, duration);
}

// ------------- Chord-aware polyphonic synthesis -------------
// Pitch-class chromagram → template matching → triad voicing → poly-osc bake.
// Strategy: for each onset window, compute a 12-bin chromagram (energy per pitch
// class C..B), match against 24 chord templates (12 major + 12 minor, root
// inversion), and synthesise the matched triad with the chosen instrument.

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const CHORD_NOTE_OFFSETS = { maj: [0, 4, 7], min: [0, 3, 7] };

function buildChordTemplates() {
  const list = [];
  for (let root = 0; root < 12; root++) {
    for (const quality of ["maj", "min"]) {
      const t = new Float32Array(12);
      for (const o of CHORD_NOTE_OFFSETS[quality]) t[(root + o) % 12] = 1;
      let n = 0;
      for (let i = 0; i < 12; i++) n += t[i] * t[i];
      n = Math.sqrt(n) || 1;
      for (let i = 0; i < 12; i++) t[i] /= n;
      list.push({ root, quality, template: t });
    }
  }
  return list;
}
const CHORD_TEMPLATES = buildChordTemplates();

function computeChroma(audioBuffer, startTime, endTime) {
  const sr = audioBuffer.sampleRate;
  const ch = audioBuffer.getChannelData(0);
  const start = Math.max(0, Math.floor(startTime * sr));
  const end = Math.min(ch.length, Math.floor(endTime * sr));
  const len = end - start;
  if (len < 256) return null;
  // Cap FFT length so we don't spend forever on long onsets.
  const useLen = Math.min(len, 4096);
  const N = nextPow2(useLen);
  const re = new Float32Array(N);
  const im = new Float32Array(N);
  for (let i = 0; i < useLen; i++) {
    const w = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (useLen - 1)));
    re[i] = (ch[start + i] || 0) * w;
  }
  fft(re, im);
  const chroma = new Float32Array(12);
  const minBin = Math.max(1, Math.floor((80 * N) / sr)); // ignore < 80 Hz (sub-bass)
  const maxBin = Math.min(N / 2, Math.floor((2000 * N) / sr)); // top at 2 kHz (harmonics dominate above)
  for (let k = minBin; k < maxBin; k++) {
    const mag = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
    if (mag < 1e-6) continue;
    const f = (k * sr) / N;
    const midi = 69 + 12 * Math.log2(f / 440);
    if (midi < 24 || midi > 96) continue;
    const pc = ((Math.round(midi) % 12) + 12) % 12;
    chroma[pc] += mag;
  }
  let norm = 0;
  for (let i = 0; i < 12; i++) norm += chroma[i] * chroma[i];
  norm = Math.sqrt(norm);
  if (norm < 1e-9) return null;
  for (let i = 0; i < 12; i++) chroma[i] /= norm;
  return chroma;
}

function matchChord(chroma, allowedRoots = null) {
  let best = null;
  let bestScore = -Infinity;
  for (const c of CHORD_TEMPLATES) {
    // Optional key filter — skip chord candidates whose root isn't a scale
    // degree of the project's key (used by Chordify's diatonic mode).
    if (allowedRoots && !allowedRoots.has(c.root)) continue;
    let s = 0;
    for (let i = 0; i < 12; i++) s += chroma[i] * c.template[i];
    if (s > bestScore) { bestScore = s; best = c; }
  }
  return {
    root: best.root,
    quality: best.quality,
    score: bestScore,
    name: `${NOTE_NAMES[best.root]}${best.quality === "min" ? "m" : ""}`,
  };
}

// Build a 4-voice chord around C4: root + 3rd + 5th, plus the root an octave up
// for richness. Returns MIDI numbers.
function chordMidis(root, quality) {
  const offsets = CHORD_NOTE_OFFSETS[quality];
  const rootMidi = 60 + root; // C4 + root semitones
  return [rootMidi, rootMidi + offsets[1], rootMidi + offsets[2], rootMidi + 12];
}

export function detectChords(audioBuffer, onsets, opts = {}) {
  if (!audioBuffer || !onsets) return [];
  // Optional key filter — when `keyRoot`/`keyMode` are provided, the matcher
  // is restricted to chord roots that are scale degrees of the given key, so
  // Chordify's output stays diatonic.
  let allowedRoots = null;
  if (opts.keyRoot !== undefined && opts.keyRoot !== null) {
    const intervals = SCALE_INTERVALS[opts.keyMode] || SCALE_INTERVALS.major;
    allowedRoots = new Set(intervals.map((d) => (opts.keyRoot + d) % 12));
  }
  const chords = [];
  for (let i = 0; i < onsets.length; i++) {
    const start = onsets[i].time;
    const end = i + 1 < onsets.length ? onsets[i + 1].time : audioBuffer.duration;
    const dur = Math.min(end - start, 0.4);
    if (dur < 0.05) continue;
    const chroma = computeChroma(audioBuffer, start, start + dur);
    if (!chroma) continue;
    const m = matchChord(chroma, allowedRoots);
    if (!m || m.score < 0.4) continue; // weak match — skip
    const explicitLen = opts.noteLengths ? (opts.noteLengths[i] ?? opts.noteLengths[String(i)]) : null;
    chords.push({
      time: start,
      // Length precedence: user-dragged block length → measured sung length,
      // both capped by the gap to the next onset.
      duration: typeof explicitLen === "number"
        ? Math.min(explicitLen, end - start)
        : measureSungDuration(audioBuffer.getChannelData(0), audioBuffer.sampleRate, start, end),
      root: m.root,
      quality: m.quality,
      name: m.name,
      midis: chordMidis(m.root, m.quality),
      energy: onsets[i].energy || 0.3,
    });
  }
  return chords;
}

// Schedule one chord-voice pitched-sample note. Per-voice amplitude is scaled
// by 1/sqrt(N) so stacking 4 voices doesn't clip the output.
function scheduleChordVoice(offline, sampleBuf, midi, t, noteDur, voice, energy, ampScale) {
  const freq = 440 * Math.pow(2, (midi - 69) / 12);
  scheduleSampleVoice(offline, sampleBuf, freq, voice, t, noteDur, energy, ampScale);
}

export async function synthesizeChords(audioBuffer, chords, instrument = "piano") {
  if (!chords || chords.length === 0) return null;
  const { voice, buffer: sampleBuf } = await loadInstrumentSample(instrument);
  const sampleRate = SAMPLE_RATE;
  const duration = audioBuffer.duration;
  const offline = new OfflineAudioContext(1, Math.ceil(duration * sampleRate) + sampleRate, sampleRate);
  for (const c of chords) {
    const t = c.time;
    const noteDur = Math.max(0.15, Math.min(c.duration, 1.5));
    if (voice.chordSample) {
      // The sample already IS a blend of strings — play one instance pitched so
      // the blend's bass anchor lands on the chord root (A2..G#3 register keeps
      // the playbackRate shift within one octave of the recording).
      const rootMidi = 45 + ((c.root - 9 + 12) % 12);
      scheduleChordVoice(offline, sampleBuf, rootMidi, t, noteDur, voice, c.energy, 1.0);
      continue;
    }
    const voiceCount = c.midis.length || 3;
    const ampScale = 1 / Math.sqrt(voiceCount);
    for (const m of c.midis) {
      scheduleChordVoice(offline, sampleBuf, m, t, noteDur, voice, c.energy, ampScale);
    }
  }
  const rendered = await offline.startRendering();
  return trimBufferTo(getAudioContext(), rendered, duration);
}

// ------------- Single-onset nudge (drag-to-quantize) -------------
// Helpers split out so the main function stays linear and reads top-to-bottom.

// 1) Slice a segment out of the source channel and apply tiny linear edge fades so
//    the moved slice doesn't click when it lands at the new location.
function captureSegment(srcData, startIdx, segSamples, fadeSamples) {
  const seg = new Float32Array(segSamples);
  for (let i = 0; i < segSamples; i++) {
    const env = edgeRamp(i, segSamples, fadeSamples);
    seg[i] = (srcData[startIdx + i] || 0) * env;
  }
  return seg;
}

// 2) Notch out the old region by multiplying it with a complementary envelope
//    (1 → 0 at the edges, 0 in the middle). Preserves adjacent audio.
function notchRegion(dstData, startIdx, segSamples, fadeSamples) {
  for (let i = 0; i < segSamples; i++) {
    const idx = startIdx + i;
    if (idx < 0 || idx >= dstData.length) continue;
    dstData[idx] = dstData[idx] * (1 - edgeRamp(i, segSamples, fadeSamples));
  }
}

// 3) Additively place the slice at the new index.
function pasteSegment(dstData, seg, newStartIdx) {
  for (let i = 0; i < seg.length; i++) {
    const idx = newStartIdx + i;
    if (idx < 0 || idx >= dstData.length) continue;
    dstData[idx] = dstData[idx] + seg[i];
  }
}

// Returns 0..1 using an equal-power raised-cosine taper: 0→1 across the first
// `fadeSamples`, flat 1 in the middle, 1→0 across the last `fadeSamples`.
// Same shape works for both the capture fade and the inverse notch (`1 - ramp`).
//
// The previous implementation was a linear ramp — its first derivative is
// discontinuous where the ramp meets the flat plateau, which reads as
// high-frequency energy at the splice point and could be mis-detected as a
// spurious onset the next time onset detection ran on the edited buffer
// (flagged by test iter_27: 6→7 onsets after a group-nudge). A raised cosine
// (Hann half-window) is C¹-continuous at both boundaries so the seam is
// inaudible AND invisible to the detector.
function edgeRamp(i, total, fadeSamples) {
  if (fadeSamples <= 0) return 1;
  // Rising edge
  if (i < fadeSamples) {
    return 0.5 * (1 - Math.cos((Math.PI * i) / fadeSamples));
  }
  // Falling edge
  const j = total - 1 - i;
  if (j < fadeSamples) {
    return 0.5 * (1 - Math.cos((Math.PI * j) / fadeSamples));
  }
  return 1;
}

// Shared segment-metadata computation used by every note-level op (nudgeOnset,
// deleteOnsetSegment, duplicateOnset, duplicateOnsetToTimes, deleteManyOnsetSegments,
// duplicateManyOnsets, snapAllOnsetsToGrid). Returns `null` when the note is too
// short to warrant an edit (≤5 ms).
//
// `noteLengths` (optional): { [i: number|string]: seconds } — per-note explicit
// length override set by the user by dragging the block's right-edge resize
// handle. When present, its value replaces the default "next-onset or 0.5 s cap"
// so a stretched block also stretches the audio slice that Nudge / Delete /
// Duplicate operate on.
function _segmentMeta(buffer, onsets, onsetIndex, noteLengths = null) {
  if (!buffer || !onsets || onsetIndex < 0 || onsetIndex >= onsets.length) return null;
  const sr = buffer.sampleRate;
  const oldStart = onsets[onsetIndex];
  const nextOnset = onsetIndex + 1 < onsets.length ? onsets[onsetIndex + 1] : buffer.duration;
  const explicit = noteLengths && (noteLengths[onsetIndex] ?? noteLengths[String(onsetIndex)]);
  const maxByBuffer = buffer.duration - oldStart;
  const maxByNext = nextOnset - oldStart;
  const defaultLen = Math.min(maxByNext, 0.5, maxByBuffer);
  const segLen = typeof explicit === "number" && explicit > 0
    ? Math.min(explicit, maxByNext, maxByBuffer)
    : defaultLen;
  if (segLen <= 0.005) return null;
  const segSamples = Math.floor(segLen * sr);
  const fadeSamples = Math.max(8, Math.min(Math.floor(0.008 * sr), Math.floor(segSamples / 4)));
  const oldStartIdx = Math.floor(oldStart * sr);
  return { sr, oldStart, segSamples, fadeSamples, oldStartIdx };
}

// Move a single detected onset from its original time to `newTime` (typically the
// nearest 1/16 grid line). Non-destructive: returns a NEW AudioBuffer of the same
// length. Strategy: capture → notch original → additively paste at newTime.
export function nudgeOnset(buffer, onsets, onsetIndex, newTime, noteLengths = null) {
  const meta = _segmentMeta(buffer, onsets, onsetIndex, noteLengths);
  if (!meta) return null;
  const { sr, segSamples, fadeSamples, oldStartIdx } = meta;
  const newStartIdx = Math.max(0, Math.min(buffer.length - segSamples, Math.floor(newTime * sr)));
  if (Math.abs(newStartIdx - oldStartIdx) < 2) return null;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    const seg = captureSegment(src, oldStartIdx, segSamples, fadeSamples);
    notchRegion(dst, oldStartIdx, segSamples, fadeSamples);
    pasteSegment(dst, seg, newStartIdx);
  }
  return out;
}

// Delete a single note: notch out the onset's segment, leave the rest untouched.
// Same edge-fade strategy as `nudgeOnset` so neighbouring audio doesn't click.
export function deleteOnsetSegment(buffer, onsets, onsetIndex, noteLengths = null) {
  const meta = _segmentMeta(buffer, onsets, onsetIndex, noteLengths);
  if (!meta) return null;
  const { sr, segSamples, fadeSamples, oldStartIdx } = meta;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    notchRegion(dst, oldStartIdx, segSamples, fadeSamples);
  }
  return out;
}

// Duplicate a single note: keep the original, additively paste a faded copy at `newTime`.
// Like `nudgeOnset` but skips the notch step so the original segment stays in place.
export function duplicateOnset(buffer, onsets, onsetIndex, newTime, noteLengths = null) {
  const meta = _segmentMeta(buffer, onsets, onsetIndex, noteLengths);
  if (!meta) return null;
  const { sr, segSamples, fadeSamples, oldStartIdx } = meta;
  const newStartIdx = Math.max(0, Math.min(buffer.length - segSamples, Math.floor(newTime * sr)));
  if (Math.abs(newStartIdx - oldStartIdx) < 2) return null;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    const seg = captureSegment(src, oldStartIdx, segSamples, fadeSamples);
    pasteSegment(dst, seg, newStartIdx);
  }
  return out;
}


// Paste the same captured segment at multiple `newTimes`. Used by the right-click
// "Duplicate to bar N" feature so a single sibling track can carry the note
// across bars 2/3/4 in one render pass.
export function duplicateOnsetToTimes(buffer, onsets, onsetIndex, newTimes, noteLengths = null) {
  if (!Array.isArray(newTimes) || newTimes.length === 0) return null;
  const meta = _segmentMeta(buffer, onsets, onsetIndex, noteLengths);
  if (!meta) return null;
  const { sr, segSamples, fadeSamples, oldStartIdx } = meta;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    const seg = captureSegment(src, oldStartIdx, segSamples, fadeSamples);
    for (const t of newTimes) {
      const idx = Math.max(0, Math.min(buffer.length - segSamples, Math.floor(t * sr)));
      if (Math.abs(idx - oldStartIdx) < 2) continue;
      pasteSegment(dst, seg, idx);
    }
  }
  return out;
}


// Copy/paste: additively paste each {index, time} pair's segment at its target
// time in a single render. Originals stay in place (copy semantics — no notch).
// `outDuration` (seconds, optional) grows the output buffer with silence so a
// paste can land beyond the take's recorded end (callers cap it at the loop
// boundary — never inflate past it, see the phantom-5th-bar rules).
export function pasteOnsetsAtTimes(buffer, onsets, pairs, noteLengths = null, outDuration = null) {
  if (!buffer || !onsets || !Array.isArray(pairs) || pairs.length === 0) return null;
  const sr = buffer.sampleRate;
  const outLen = Math.max(buffer.length, Math.floor((outDuration || 0) * sr));
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, outLen, sr);
  let pasted = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    for (const { index, time } of pairs) {
      const meta = _segmentMeta(buffer, onsets, index, noteLengths);
      if (!meta) continue;
      const idx = Math.max(0, Math.min(outLen - meta.segSamples, Math.floor(time * sr)));
      if (Math.abs(idx - meta.oldStartIdx) < 2) continue;
      pasteSegment(dst, captureSegment(src, meta.oldStartIdx, meta.segSamples, meta.fadeSamples), idx);
      if (c === 0) pasted++;
    }
  }
  return pasted > 0 ? out : null;
}


// Make a note RING exactly `lengthSec`: raised-cosine fade (20 ms) ending at
// onset+lengthSec, then silence until the next onset (or buffer end). Returns
// null when there's nothing audible to cut (length ≥ gap, or tail already
// silent) so the caller can fall back to a metadata-only update.
export function applyNoteRingLength(buffer, onsets, onsetIndex, lengthSec) {
  if (!buffer || !onsets) return null;
  const t = onsets[onsetIndex];
  if (typeof t !== "number" || !(lengthSec > 0)) return null;
  const sr = buffer.sampleRate;
  const nextT = onsetIndex + 1 < onsets.length ? onsets[onsetIndex + 1] : buffer.duration;
  const gapEnd = Math.min(buffer.length, Math.floor(nextT * sr));
  const cutIdx = Math.floor((t + lengthSec) * sr);
  if (cutIdx >= gapEnd - 8) return null; // note already ends here or later
  // Skip when the region past the cut is effectively silent already.
  let peak = 0;
  const ch0 = buffer.getChannelData(0);
  for (let i = cutIdx; i < gapEnd; i++) {
    const a = Math.abs(ch0[i]);
    if (a > peak) peak = a;
  }
  if (peak < 0.004) return null;
  const fadeSamples = Math.min(Math.floor(0.02 * sr), Math.max(8, cutIdx - Math.floor(t * sr)));
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    for (let i = 0; i < fadeSamples; i++) {
      const idx = cutIdx - fadeSamples + i;
      if (idx < 0 || idx >= dst.length) continue;
      dst[idx] = src[idx] * 0.5 * (1 + Math.cos((Math.PI * i) / fadeSamples));
    }
    dst.fill(0, Math.max(0, cutIdx), gapEnd);
  }
  return out;
}


// Batch delete: notch every selected onset's segment in a single sibling render.
// Complements the single-note `deleteOnsetSegment`.
export function deleteManyOnsetSegments(buffer, onsets, indices, noteLengths = null) {
  if (!buffer || !onsets || !indices || indices.length === 0) return null;
  const sr = buffer.sampleRate;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    for (const idx of indices) {
      const meta = _segmentMeta(buffer, onsets, idx, noteLengths);
      if (!meta) continue;
      notchRegion(dst, meta.oldStartIdx, meta.segSamples, meta.fadeSamples);
    }
  }
  return out;
}


// Batch duplicate: for each selected onset, additively paste a faded copy shifted
// by `shiftStep` seconds (default = 1/16 note in current bar). Original notes
// stay in place. Complements the single-note `duplicateOnset`.
export function duplicateManyOnsets(buffer, onsets, indices, shiftStep, noteLengths = null) {
  if (!buffer || !onsets || !indices || indices.length === 0) return null;
  if (!shiftStep || shiftStep <= 0) return null;
  const sr = buffer.sampleRate;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    for (const idx of indices) {
      const meta = _segmentMeta(buffer, onsets, idx, noteLengths);
      if (!meta) continue;
      const newStartIdx = Math.max(0, Math.min(buffer.length - meta.segSamples, Math.floor((meta.oldStart + shiftStep) * sr)));
      if (Math.abs(newStartIdx - meta.oldStartIdx) < 2) continue;
      const seg = captureSegment(src, meta.oldStartIdx, meta.segSamples, meta.fadeSamples);
      pasteSegment(dst, seg, newStartIdx);
    }
  }
  return out;
}


// Batch nudge: shift every selected onset by the same `deltaSec`. Non-destructive:
// captures each segment, notches the originals, additively pastes at shifted
// positions. Used when dragging a note in a multi-selection so the whole group
// moves together while preserving relative timing.
export function nudgeManyOnsets(buffer, onsets, indices, deltaSec, noteLengths = null) {
  if (!buffer || !onsets || !indices || indices.length === 0) return null;
  if (!deltaSec || Math.abs(deltaSec) < 0.001) return null;
  const sr = buffer.sampleRate;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    // First pass: notch all source positions so we don't hear both original + shifted.
    const captures = [];
    for (const idx of indices) {
      const meta = _segmentMeta(buffer, onsets, idx, noteLengths);
      if (!meta) { captures.push(null); continue; }
      captures.push({ meta, seg: captureSegment(src, meta.oldStartIdx, meta.segSamples, meta.fadeSamples) });
      notchRegion(dst, meta.oldStartIdx, meta.segSamples, meta.fadeSamples);
    }
    // Second pass: paste at shifted positions.
    for (let i = 0; i < indices.length; i++) {
      const cap = captures[i];
      if (!cap) continue;
      const { meta, seg } = cap;
      const newStartIdx = Math.max(0, Math.min(buffer.length - meta.segSamples, Math.floor((meta.oldStart + deltaSec) * sr)));
      pasteSegment(dst, seg, newStartIdx);
    }
  }
  return out;
}


// Pitch shift a single onset in place by `semitones`. Uses the browser's
// built-in resampler via `OfflineAudioContext.playbackRate` — same high-quality
// path the instrument voices use. Length is held constant: pitching up ends
// slightly earlier (trailing silence), pitching down truncates the tail. Both
// are acceptable for melody-note pitch correction (±1-2 semitones typical).
export async function pitchShiftOnset(buffer, onsets, onsetIndex, semitones, noteLengths = null) {
  const meta = _segmentMeta(buffer, onsets, onsetIndex, noteLengths);
  if (!meta) return null;
  if (Math.abs(semitones) < 0.001) return null;
  const { sr, segSamples, fadeSamples, oldStartIdx } = meta;
  const rate = Math.pow(2, semitones / 12);

  // Copy the raw segment into a mini-buffer for the offline resampler.
  const segBuf = getAudioContext().createBuffer(buffer.numberOfChannels, segSamples, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = segBuf.getChannelData(c);
    for (let i = 0; i < segSamples; i++) dst[i] = src[oldStartIdx + i] || 0;
  }

  // Render the shifted segment at the target length (same as source length).
  const offline = new OfflineAudioContext(buffer.numberOfChannels, segSamples, sr);
  const sourceNode = offline.createBufferSource();
  sourceNode.buffer = segBuf;
  sourceNode.playbackRate.value = rate;
  sourceNode.connect(offline.destination);
  sourceNode.start(0);
  const shiftedBuf = await offline.startRendering();

  // Splice shifted result back into a copy of the parent buffer with a click-free
  // seam (cosine edge ramp) — matches how nudge/delete/duplicate splice.
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    const shifted = shiftedBuf.getChannelData(Math.min(c, shiftedBuf.numberOfChannels - 1));
    // Apply the standard edge fade to the shifted content before splicing.
    const faded = new Float32Array(segSamples);
    for (let i = 0; i < segSamples; i++) {
      faded[i] = shifted[i] * edgeRamp(i, segSamples, fadeSamples);
    }
    notchRegion(dst, oldStartIdx, segSamples, fadeSamples);
    pasteSegment(dst, faded, oldStartIdx);
  }
  return out;
}


// Batch pitch shift: shift every selected onset by the same `semitones`.
// Same OfflineAudioContext technique as the single-onset version, run per note.
export async function pitchShiftManyOnsets(buffer, onsets, indices, semitones, noteLengths = null) {
  if (!buffer || !onsets || !indices || indices.length === 0) return null;
  if (Math.abs(semitones) < 0.001) return null;
  // Delegate to the per-note engine so every hit gets the same shift value.
  return _pitchShiftPerNote(buffer, onsets, indices.map((i) => ({ idx: i, semi: semitones })), noteLengths);
}

// Musical scale intervals as semitone offsets from the key root.
const SCALE_INTERVALS = {
  major:      [0, 2, 4, 5, 7, 9, 11],
  minor:      [0, 2, 3, 5, 7, 8, 10],   // natural minor
  dorian:     [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  pentatonic: [0, 2, 4, 7, 9],
};

// Auto-tune: for each selected onset, detect its pitch via autocorrelation and
// compute the shortest semitone shift that lands on the nearest in-key pitch.
// `keyRoot` is 0..11 (C=0, C#=1, D=2, …, B=11). `keyMode` is one of the keys
// in `SCALE_INTERVALS`. Notes we can't confidently pitch-detect (unvoiced /
// silent / out-of-range) are left untouched.
export async function autoTuneOnsets(buffer, onsets, indices, keyRoot, keyMode, noteLengths = null) {
  if (!buffer || !onsets || !indices || indices.length === 0) return null;
  const intervals = SCALE_INTERVALS[keyMode] || SCALE_INTERVALS.major;
  const sr = buffer.sampleRate;
  const channel = buffer.getChannelData(0);
  const windowLen = Math.floor(0.08 * sr);
  const plan = [];
  for (const idx of indices) {
    const meta = _segmentMeta(buffer, onsets, idx, noteLengths);
    if (!meta) continue;
    const freq = autocorrelatePitch(channel, sr, meta.oldStartIdx, windowLen);
    if (!freq || freq < 60 || freq > 1200) continue;
    const midi = 69 + 12 * Math.log2(freq / 440);
    const pitchClass = ((Math.round(midi) % 12) + 12) % 12;
    const relDegree = ((pitchClass - keyRoot) % 12 + 12) % 12;
    // Search all wraparound-neighbours for the smallest |delta|.
    let bestDelta = 0;
    let bestAbs = Infinity;
    for (const deg of intervals) {
      for (const wrap of [-12, 0, 12]) {
        const delta = (deg + wrap) - relDegree;
        if (Math.abs(delta) < bestAbs) { bestAbs = Math.abs(delta); bestDelta = delta; }
      }
    }
    if (Math.abs(bestDelta) >= 0.5) plan.push({ idx, semi: bestDelta });
  }
  if (plan.length === 0) return null;
  return _pitchShiftPerNote(buffer, onsets, plan, noteLengths);
}

// Internal: given a plan of `{ idx, semi }` entries, splice each shifted note
// back into a fresh copy of the parent buffer using the shared OfflineAudioContext
// resampler and cosine edge fade. Powers both pitchShiftManyOnsets (uniform
// shift) and autoTuneOnsets (per-note shift).
async function _pitchShiftPerNote(buffer, onsets, plan, noteLengths) {
  const sr = buffer.sampleRate;
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    out.getChannelData(c).set(buffer.getChannelData(c));
  }
  for (const { idx, semi } of plan) {
    if (Math.abs(semi) < 0.001) continue;
    const meta = _segmentMeta(buffer, onsets, idx, noteLengths);
    if (!meta) continue;
    const { segSamples, fadeSamples, oldStartIdx } = meta;
    const rate = Math.pow(2, semi / 12);
    const segBuf = getAudioContext().createBuffer(buffer.numberOfChannels, segSamples, sr);
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      const src = buffer.getChannelData(c);
      const dst = segBuf.getChannelData(c);
      for (let i = 0; i < segSamples; i++) dst[i] = src[oldStartIdx + i] || 0;
    }
    const offline = new OfflineAudioContext(buffer.numberOfChannels, segSamples, sr);
    const sourceNode = offline.createBufferSource();
    sourceNode.buffer = segBuf;
    sourceNode.playbackRate.value = rate;
    sourceNode.connect(offline.destination);
    sourceNode.start(0);
    const shiftedBuf = await offline.startRendering();
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      const dst = out.getChannelData(c);
      const shifted = shiftedBuf.getChannelData(Math.min(c, shiftedBuf.numberOfChannels - 1));
      const faded = new Float32Array(segSamples);
      for (let i = 0; i < segSamples; i++) faded[i] = shifted[i] * edgeRamp(i, segSamples, fadeSamples);
      notchRegion(dst, oldStartIdx, segSamples, fadeSamples);
      pasteSegment(dst, faded, oldStartIdx);
    }
  }
  return out;
}



// Section copy: duplicate the first `halfDur` seconds (bars 1–4) onto the
// second half (bars 5–8). Returns an exactly 2×halfDur buffer — trailing
// silence in the source's first half is copied too, keeping bar alignment.
export function copyFirstHalfToSecondHalf(buffer, halfDur) {
  if (!buffer || halfDur <= 0) return null;
  const sr = buffer.sampleRate;
  const half = Math.floor(halfDur * sr);
  const out = getAudioContext().createBuffer(buffer.numberOfChannels, half * 2, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    const n = Math.min(half, src.length);
    for (let i = 0; i < n; i++) {
      const v = src[i];
      dst[i] = v;
      dst[i + half] = v;
    }
  }
  return out;
}


// Pitch preview: instantly audition freshly shifted note(s) without starting
// full playback. Plays from the earliest selected note through the end of the
// last selected note's segment (capped at 3s). Rapid re-pitching restarts it.
let _previewSrc = null;
export function previewNoteSegments(buffer, onsets, indices, noteLengths = null, volume = 1) {
  if (!buffer || !onsets || !indices || indices.length === 0) return;
  const valid = indices.filter((i) => i >= 0 && i < onsets.length);
  if (!valid.length) return;
  const startSec = Math.min(...valid.map((i) => onsets[i]));
  let endSec = startSec + 0.5;
  for (const i of valid) {
    const meta = _segmentMeta(buffer, onsets, i, noteLengths);
    if (meta) endSec = Math.max(endSec, onsets[i] + meta.segSamples / meta.sr);
  }
  try { _previewSrc?.stop(); } catch { /* already stopped */ }
  const ctx = getAudioContext();
  if (ctx.state === "suspended") ctx.resume();
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const g = ctx.createGain();
  g.gain.value = volume;
  src.connect(g).connect(ctx.destination);
  src.start(0, Math.max(0, startSec), Math.max(0.05, Math.min(endSec - startSec, 3)));
  _previewSrc = src;
}


// Snap every onset in a buffer to the nearest grid line of `snapStep` seconds.
// Non-destructive: returns a NEW same-length AudioBuffer rebuilt from silence,
// with each detected note's captured segment additively pasted at its snapped
// position (using the same edge-fade capture helper as `nudgeOnset`).
// Returns null if nothing meaningfully moved (every hit was already on-grid).
export function snapAllOnsetsToGrid(buffer, onsets, snapStep, loopDuration, noteLengths = null) {
  if (!buffer || !onsets || onsets.length === 0) return null;
  if (!snapStep || snapStep <= 0) return null;
  const sr = buffer.sampleRate;
  const maxTime = Math.min(buffer.duration, loopDuration && loopDuration > 0 ? loopDuration : buffer.duration);

  // Snap each onset to nearest grid line, clamped to the loop window.
  const targets = onsets.map((t) => {
    const snapped = Math.round(t / snapStep) * snapStep;
    return Math.max(0, Math.min(maxTime, snapped));
  });

  // Bail out if nothing moved ≥ 2 samples (avoids re-rendering identical audio).
  const anyMoved = targets.some((newT, i) =>
    Math.abs(Math.floor(newT * sr) - Math.floor(onsets[i] * sr)) >= 2
  );
  if (!anyMoved) return null;

  const out = getAudioContext().createBuffer(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c); // starts silent
    for (let i = 0; i < onsets.length; i++) {
      const meta = _segmentMeta(buffer, onsets, i, noteLengths);
      if (!meta) continue;
      const newStartIdx = Math.max(0, Math.min(buffer.length - meta.segSamples, Math.floor(targets[i] * sr)));
      const seg = captureSegment(src, meta.oldStartIdx, meta.segSamples, meta.fadeSamples);
      pasteSegment(dst, seg, newStartIdx);
    }
  }
  return out;
}


// Helper: encode AudioBuffer back to a wav blob suitable to upload
export function audioBufferToWav(buffer) {
  return audioBufferToWavBlob(buffer);
}
